export type HumanizedApiError = {
  title: string;
  message: string;
  statusCode?: number;
};

function tryParseJsonObject(raw: string): Record<string, unknown> | null {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

function extractJsonMessage(parsed: Record<string, unknown>): string | null {
  const msg = parsed.message;
  if (typeof msg === 'string') return msg;
  if (Array.isArray(msg)) {
    const parts = msg.map((v) => (typeof v === 'string' ? v.trim() : '')).filter(Boolean);
    return parts.length ? parts.join('\n') : null;
  }
  return null;
}

function extractStatusCode(parsed: Record<string, unknown>): number | undefined {
  const code = parsed.statusCode;
  if (typeof code === 'number' && Number.isFinite(code)) return code;
  if (typeof code === 'string') {
    const n = Number(code);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function normalizeErrorText(input: unknown): { raw: string; statusCode?: number } {
  if (input instanceof Error) return { raw: input.message || 'Unknown error' };
  if (typeof input === 'string') {
    const parsed = tryParseJsonObject(input);
    if (!parsed) return { raw: input };
    const msg = extractJsonMessage(parsed) ?? input;
    const statusCode = extractStatusCode(parsed);
    return { raw: msg, statusCode };
  }
  if (input && typeof input === 'object') {
    const maybeMsg = (input as any).message;
    if (typeof maybeMsg === 'string') return { raw: maybeMsg };
  }
  return { raw: String(input ?? 'Unknown error') };
}

export function humanizeApiError(input: unknown): HumanizedApiError {
  const { raw, statusCode } = normalizeErrorText(input);
  const normalized = raw.toLowerCase();

  const looksUnauthorized = statusCode === 401 || normalized.includes('unauthorized');
  if (looksUnauthorized) {
    return {
      title: 'Sign-in required',
      message: 'Your session may have expired. Please sign in again and retry.',
      statusCode,
    };
  }

  const looksForbidden = statusCode === 403 || normalized.includes('forbidden');
  if (looksForbidden) {
    return {
      title: 'Access denied',
      message: 'You do not have access to this workspace or resource.',
      statusCode,
    };
  }

  const looksMissingExecutionRun =
    normalized.includes('no execution run found') || normalized.includes('no execution cockpit run found');
  if (looksMissingExecutionRun) {
    return {
      title: 'Run required',
      message: 'Run Decision Console for this strategy once, then return to Daily Brief.',
      statusCode,
    };
  }

  const looksInvalidSavedExecutionRun = normalized.includes('saved execution run') && normalized.includes('invalid');
  if (looksInvalidSavedExecutionRun) {
    return {
      title: 'Re-run required',
      message: 'Your saved execution run configuration is invalid. Run Decision Console again to regenerate it.',
      statusCode,
    };
  }

  const looksNoNewerWorkSignalsSnapshot = normalized.includes('no newer work-signal snapshot is available yet');
  if (looksNoNewerWorkSignalsSnapshot) {
    return {
      title: 'No newer snapshot',
      message: raw,
      statusCode,
    };
  }

  const looksMissingJiraSnapshot =
    normalized.includes('jira snapshot artifact') || normalized.includes('configure jira') || normalized.includes('jira snapshot');
  if (looksMissingJiraSnapshot) {
    return {
      title: 'Jira snapshot required',
      message: 'This strategy does not have a Jira snapshot yet. Configure Jira and build a snapshot, then retry.',
      statusCode,
    };
  }

  const looksMissingExecutionCockpitConfig =
    normalized.includes('execution cockpit config') ||
    normalized.includes('configure execution cockpit') ||
    normalized.includes('execution cockpit initiatives') ||
    normalized.includes('initiatives first');
  if (looksMissingExecutionCockpitConfig) {
    return {
      title: 'Execution cockpit setup required',
      message: 'This strategy does not have an initiative set yet. Configure initiatives, then rerun Decision Console.',
      statusCode,
    };
  }

  const looksMissingExecutiveBriefEndpoint =
    normalized.includes('cannot get /v1/executive-brief') ||
    normalized.includes('/v1/executive-brief') && normalized.includes('not found');
  if (looksMissingExecutiveBriefEndpoint) {
    return {
      title: 'Executive Brief unavailable',
      message:
        'This environment is not serving the Executive Brief API yet. Start the matching API branch on port 4000, then retry.',
      statusCode: statusCode ?? 404,
    };
  }

  const looksNotFound =
    statusCode === 404 ||
    normalized.includes('not found') ||
    normalized.includes('cannot find') ||
    normalized.includes('no strategy');

  if (looksNotFound) {
    if (normalized.includes('strategy')) {
      return {
        title: 'Strategy not found',
        message:
          "This link points to a strategy that isn't available in your current workspace. Pick a strategy from Strategies and try again.",
        statusCode: statusCode ?? 404,
      };
    }

    return {
      title: 'Not found',
      message: "We couldn't find what you're looking for in this workspace.",
      statusCode: statusCode ?? 404,
    };
  }

  return {
    title: 'Unable to load',
    message: 'Something went wrong while loading this page. Please retry.',
    statusCode,
  };
}

